import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import alertify from 'alertifyjs';
import { Subscription } from 'rxjs';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import { LandingNavbarComponent } from '../../../components/landing-navbar/landing-navbar.component';

type DocumentVerificationType = 'certificate' | 'payment' | 'demand_notice';
type VerificationState = 'idle' | 'loading' | 'found' | 'not_found' | 'error';

type VerificationMeta = {
  vendor_id: string;
  vendor_name: string;
  vendor_reference: string;
  vendor_type: string;
  address?: string;
  approval_status: string;
  mda_id: string;
  mda_name: string;
  category_id: string;
  category_name: string;
};

type CertificateVerificationData = VerificationMeta & {
  message: string;
  certificate_no: string;
  status: string;
  year: number;
  valid_from: string;
  valid_to: string;
  validity_period: string;
  payment_status: string;
  payment_reference: string;
};

type PaymentVerificationData = VerificationMeta & {
  message: string;
  invoice_number: string;
  payment_reference: string;
  status: string;
  year: number;
  payment_date: string;
  amount: string;
  payment_channel: string;
  payment_method: string;
  payer_email: string;
  revenue_name: string;
  asin: string;
};

type DemandNoticeVerificationData = VerificationMeta & {
  message: string;
  notice_id: string;
  status: string;
  notice_date: string;
  application_type: string;
  amount_due: string;
  payment_status: string;
  payment_reference: string;
  instructions: string;
  asin: string;
  email: string;
  phone_no: string;
};

type VerificationResponse =
  | {
      status: 'success';
      message: 'FOUND';
      data: CertificateVerificationData | PaymentVerificationData | DemandNoticeVerificationData;
    }
  | {
      status: 'success';
      message: 'NOT_FOUND';
      data: null;
    };

type DetailRow = {
  label: string;
  value: string;
  tone?: 'default' | 'success' | 'warning' | 'danger';
};

@Component({
  selector: 'app-public-verify-document',
  standalone: true,
  imports: [CommonModule, RouterLink, LandingNavbarComponent],
  templateUrl: './public-verify-document.component.html',
  styleUrl: './public-verify-document.component.scss',
})
export class PublicVerifyDocumentComponent implements OnInit, OnDestroy {
  state: VerificationState = 'idle';
  documentType: DocumentVerificationType | '' = '';
  rawValue = '';
  normalizedValue = '';
  title = 'Document Verification';
  subtitle = 'Scan a QR code or open a verification link to confirm a document.';
  errorMessage = '';
  resultRows: DetailRow[] = [];
  resultBadge = '';
  resultTone: 'success' | 'warning' | 'danger' = 'warning';
  resultSummary = '';

  private readonly sub = new Subscription();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly apiService: ApiServiceService,
  ) {
    alertify.set('notifier', 'position', 'top-right');
    alertify.set('notifier', 'delay', 4);
  }

  ngOnInit(): void {
    this.sub.add(
      this.route.queryParamMap.subscribe((params) => {
        const type = this.normalizeType(params.get('type'));
        const value = this.normalizeValue(params.get('value'));

        this.documentType = type;
        this.rawValue = params.get('value') ?? '';
        this.normalizedValue = value;

        if (!type || !value) {
          this.state = 'error';
          this.errorMessage =
            'Provide both a document type and a QR value to verify this record.';
          this.resetResult();
          return;
        }

        this.verifyDocument(type, value);
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get typeLabel(): string {
    return this.getTypeLabel(this.documentType);
  }

  retry(): void {
    if (!this.documentType || !this.normalizedValue) return;
    this.verifyDocument(this.documentType, this.normalizedValue);
  }

  private verifyDocument(type: DocumentVerificationType, value: string): void {
    this.state = 'loading';
    this.errorMessage = '';
    this.resetResult();

    this.apiService.post('/public/vendor-verification', { type, value }).subscribe({
      next: (response: VerificationResponse | any) => {
        const message = String(response?.message ?? '').toUpperCase();
        const data = response?.data ?? null;

        if (message !== 'FOUND' || !data) {
          this.state = 'not_found';
          this.resultBadge = 'Not found';
          this.resultTone = 'warning';
          this.resultSummary =
            'No matching document was found for the scanned QR code.';
          return;
        }

        this.state = 'found';
        this.resultTone = 'success';
        this.resultBadge = 'Verified';
        this.resultSummary = this.getSuccessSummary(type, data);
        this.resultRows = this.buildResultRows(type, data);
      },
      error: (error) => {
        this.state = 'error';
        this.errorMessage =
          typeof error === 'string'
            ? error
            : 'We could not verify this document right now. Please try again.';
      },
    });
  }

  private buildResultRows(
    type: DocumentVerificationType,
    data: CertificateVerificationData | PaymentVerificationData | DemandNoticeVerificationData,
  ): DetailRow[] {
    const shared: DetailRow[] = [
      { label: 'Vendor name', value: data.vendor_name || 'N/A' },
      { label: 'Vendor reference', value: data.vendor_reference || 'N/A' },
      { label: 'Vendor type', value: data.vendor_type || 'N/A' },
      {
        label: 'Approval status',
        value: data.approval_status || 'N/A',
        tone: this.getStatusTone(data.approval_status),
      },
      { label: 'MDA', value: data.mda_name || 'N/A' },
      { label: 'Category', value: data.category_name || 'N/A' },
    ];

    if (type === 'certificate') {
      const cert = data as CertificateVerificationData;
      const showCertificatePaymentReference = false;
      return [
        { label: 'Certificate no.', value: cert.certificate_no || 'N/A' },
        { label: 'Status', value: cert.status || 'N/A', tone: cert.status === 'ACTIVE' ? 'success' : 'warning' },
        { label: 'Validity period', value: cert.validity_period || 'N/A' },
        { label: 'Valid from', value: this.formatDate(cert.valid_from) },
        { label: 'Valid to', value: this.formatDate(cert.valid_to) },
        {
          label: 'Payment status',
          value: cert.payment_status || 'N/A',
          tone: this.getStatusTone(cert.payment_status),
        },
        ...(showCertificatePaymentReference
          ? [{ label: 'Payment reference', value: cert.payment_reference || 'N/A' }]
          : []),
        ...shared,
        { label: 'Address', value: cert.address || 'N/A' },
      ];
    }

    if (type === 'payment') {
      const payment = data as PaymentVerificationData;
      return [
        { label: 'Invoice number', value: payment.invoice_number || 'N/A' },
        { label: 'Payment reference', value: payment.payment_reference || 'N/A' },
        { label: 'Status', value: payment.status || 'N/A', tone: this.getStatusTone(payment.status) },
        { label: 'Amount', value: this.formatMoney(payment.amount) },
        { label: 'Payment date', value: this.formatDate(payment.payment_date) },
        { label: 'Payment channel', value: payment.payment_channel || 'N/A' },
        { label: 'Payment method', value: payment.payment_method || 'N/A' },
        { label: 'Payer email', value: payment.payer_email || 'N/A' },
        { label: 'Revenue name', value: payment.revenue_name || 'N/A' },
        { label: 'ASIN', value: payment.asin || 'N/A' },
        ...shared,
        { label: 'Address', value: payment.address || 'N/A' },
      ];
    }

    const notice = data as DemandNoticeVerificationData;
    return [
      { label: 'Notice ID', value: notice.notice_id || 'N/A' },
      { label: 'Status', value: notice.status || 'N/A', tone: notice.status === 'NOT_PAID' ? 'warning' : 'success' },
      { label: 'Notice date', value: this.formatDate(notice.notice_date) },
      { label: 'Application type', value: notice.application_type || 'N/A' },
      { label: 'Amount due', value: this.formatMoney(notice.amount_due) },
      {
        label: 'Payment status',
        value: notice.payment_status || 'N/A',
        tone: this.getStatusTone(notice.payment_status),
      },
      { label: 'Payment reference', value: notice.payment_reference || 'N/A' },
      { label: 'Instructions', value: notice.instructions || 'N/A' },
      { label: 'ASIN', value: notice.asin || 'N/A' },
      { label: 'Email', value: notice.email || 'N/A' },
      { label: 'Phone number', value: notice.phone_no || 'N/A' },
      ...shared,
      { label: 'Address', value: notice.address || 'N/A' },
    ];
  }

  private getSuccessSummary(
    type: DocumentVerificationType,
    data: CertificateVerificationData | PaymentVerificationData | DemandNoticeVerificationData,
  ): string {
    if (type === 'certificate') {
      const cert = data as CertificateVerificationData;
      return `${cert.vendor_name} has a valid certificate for ${cert.validity_period}.`;
    }

    if (type === 'payment') {
      const payment = data as PaymentVerificationData;
      return `${payment.vendor_name} has a verified payment record of ${this.formatMoney(payment.amount)}.`;
    }

    const notice = data as DemandNoticeVerificationData;
    return `${notice.vendor_name} has a demand notice with status ${notice.status}.`;
  }

  private getStatusTone(status: string): 'success' | 'warning' | 'danger' {
    const normalized = String(status ?? '').trim().toUpperCase();
    if (['ACTIVE', 'APPROVED', 'PAID', 'FOUND'].includes(normalized)) {
      return 'success';
    }

    if (['NOT_PAID', 'PENDING', 'NOT_FOUND'].includes(normalized)) {
      return 'warning';
    }

    return 'danger';
  }

  private normalizeType(value: string | null): DocumentVerificationType | '' {
    const normalized = String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[-\s]+/g, '_');

    if (normalized === 'certificate' || normalized === 'payment' || normalized === 'demand_notice') {
      return normalized;
    }

    return '';
  }

  private normalizeValue(value: string | null): string {
    const raw = String(value ?? '').replace(/\+/g, ' ');

    try {
      return decodeURIComponent(raw).trim();
    } catch {
      return raw.trim();
    }
  }

  private formatDate(value: string): string {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }

  private formatMoney(value: string): string {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return value || 'N/A';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      maximumFractionDigits: 2,
    }).format(amount);
  }

  private getTypeLabel(type: DocumentVerificationType | ''): string {
    switch (type) {
      case 'certificate':
        return 'Certificate verification';
      case 'payment':
        return 'Payment verification';
      case 'demand_notice':
        return 'Demand notice verification';
      default:
        return 'Document verification';
    }
  }

  private resetResult(): void {
    this.resultRows = [];
    this.resultBadge = '';
    this.resultSummary = '';
  }
}
